import React, { useState, useEffect } from "react";
import { AlarmData } from "../Common/Types/types";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../Common/Components/spinner";
import ErrorMessage from "../Common/Components/ErrorMessage";
import { useAppDispatch } from "../Core/store.hooks";
import AlarmComboChart from "../Common/Components/Charts/ComboChart";
import {getParamsSelector, updateParams } from "../Common/Slices/ParamSlice";
import Format from "./FormatMachineTimes"
import { getDevicesSelector } from "../Devices/Slice";
import { getMachineTimesLoadingSelector, getMachineTimesSelector } from "./slice";
import { findKey, orderBy } from "lodash";
import { getOptionsSelector } from "../Common/Slices/optionSlice";
import { getAlarmsLoadingSelectorWithScope, getAlarmsSelector, getAlarmsWithScopeSelector } from "../Alarms/Slice";
import { machineTimesToHours, msToHours } from "../Common/Util_methods/format";
import { paramsById } from "../Common/Util_methods/scope";



interface ChartWidgetProps {
    id: string;
    header: string;
    values?: "machineTimes" | "machineTimesPerDay";
    series?: {
        name:string,
        unit:string,
        color:string
        }
}

const AlarmStatisticsChartWidget = (props: ChartWidgetProps) => {
    const [combined,setCombined] = useState<boolean>(false);
    const [chartData, updateChartData] = useState([]);
    const [dataProcessed, setDataProcessed] = useState(false);

    const machineTimesData = useSelector(getMachineTimesSelector);
    const alarmData = useSelector(getAlarmsWithScopeSelector);
    const alarmsLoading = useSelector(getAlarmsLoadingSelectorWithScope);
    const loadingState = useSelector(getMachineTimesLoadingSelector)
    const options = useSelector(getOptionsSelector);
    const params = useSelector(getParamsSelector);
    const deviceObjects = useSelector(getDevicesSelector);

    const dispatch = useAppDispatch();

    useEffect(()=>{
        if(props.values === "machineTimesPerDay" || options.alarmsVisibility === false){
            if(machineTimesData.length > 0){
                const sortedData = sortData(machineTimesData)
                updateChartData(sortedData);
            }else{
                updateChartData([])
            }
            setDataProcessed(true);
        }
    },[options.machineTimesSortOrder,options.chartItemNumber,machineTimesData])

    useEffect(()=>{
        if(options.alarmsVisibility === true && loadingState !== "pending" && alarmsLoading === "fulfilled" && props.values === "machineTimes"){
            if(machineTimesData.length > 0){
                const combinedData = combineData();
                const sortedData = sortData(combinedData)
                updateChartData(sortedData);
                setCombined(true)
            }else{
                updateChartData([])
            }
            setDataProcessed(true);
        }
    },[options.alarmsVisibility,options.machineTimesSortOrder,options.chartItemNumber,machineTimesData,alarmData])

    useEffect(()=>{
        if (options.alarmsVisibility === false && loadingState !== "pending"){
            if(machineTimesData.some((obj)=>obj.scope === "cadid")){
                const dataInHours = machineTimesToHours(machineTimesData);
                
                updateChartData(dataInHours);
            }else{
                updateChartData(sortData(machineTimesData))
             }
            setCombined(false)
            setDataProcessed(true);
        }
    },[machineTimesData, options.chartItemNumber, options.machineTimesSortOrder,options.alarmsVisibility])

    useEffect(()=>{
        if(loadingState === "pending"){
            setDataProcessed(false);
        }
        if(loadingState === "rejected"){
            setDataProcessed(true);
        }
        if(loadingState === "fulfilled" && machineTimesData.length === 0){
            setDataProcessed(true);
        }
    },[loadingState])

    const sortData = (data:any) => {
        let sortedData = data;
        function getObjKey(obj:any, value:string) {
            return Object.keys(obj).find(key => obj[key] === value);
          }
        const totalChartKeys = {
            shiftDuration: "Shift Length",
            runningDuration: "Running Hours",
            failureDuration: "Failure Hours",
            failureTimeoutDuration: "Failure Timeout",
            idleAfterRunningDuration:"Idle after Running",
            idleAfterFailureDuration:"Idle after Failure",
            failureToRunningRatio: "Failure to running %",
            failureToShiftRatio: "Failure to Shift %"
        }
        const dailyChartKeys = {
            shiftDurationPerDay: "Shift Length",
            runningDurationPerDay: "Running Hours",
            failureDurationPerDay: "Failure Hours",
            failureTimeoutDurationPerDay: "Failure Timeout",
            idleAfterRunningDurationPerDay:"Idle after Running",
            idleAfterFailureDurationPerDay:"Idle after Failure",
            failureToRunningRatioPerDay: "Failure to running %",
            failureToShiftRatioPerDay: "Failure to Shift %"
        }
        const dataInHours = Format.durationsToHours(data);

        if(props.values === "machineTimes"){
            const key = getObjKey(totalChartKeys,options.machineTimesSortOrder)
            sortedData = orderBy(dataInHours,key,"desc")
        }
        if(props.values === "machineTimesPerDay"){
            const key = getObjKey(dailyChartKeys,options.machineTimesSortOrder)
            sortedData = orderBy(dataInHours,key,"desc")
        }
        return sortedData;
    }

    const handleChartClick = (id:any) =>{
        
        if (id != null){
            const newParams = paramsById(params,options,deviceObjects,id)
            if(newParams !== null){
                dispatch(updateParams(newParams))
            }
        }
    }

    const combineData = () =>{
        let combinedData = []
        for(let i=0; i<machineTimesData.length; i++) {
            combinedData.push({
                ...machineTimesData[i], 
                ...(alarmData.find((obj) => obj.id === machineTimesData[i].id))}
                );
        }
        combinedData = combinedData.map((obj)=>{
            let copy = {...obj}
            copy.duration = msToHours(obj.duration)
            return copy
        })
        return combinedData;
    }

    if (!dataProcessed) return <Spinner text="data" />;
    if (chartData == null  || loadingState === "rejected") {
        //proper error handling should be added with promise error response
        return (
            <ErrorMessage
                text={"Oops something went wrong, unable to get data."}
                id={props.id}
                header={props.header}
            />
        );
    }   
    if (chartData != null && chartData.length === 0) {
        return (
            <ErrorMessage
                text={"No data found for selected filters."}
                id={props.id}
                header={props.header}
            />
        );
    }
    return <AlarmComboChart 
        type={props.values}
        clickHandler={handleChartClick} 
        combined={combined}
        id={props.id} 
        category="text" 
        data={chartData} 
        series={{name:"Machine times", unit:"Hours", color:"black"}}
        itemNumber={options.chartItemNumber}
        values = "duration"
        />;
};
export default AlarmStatisticsChartWidget;
